import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { AlertSubscriptionViewStore } from "../../store";
import { AlertSubscriptionView } from "../view/AlertSubscriptionView";
import { AlertSubscriptionUpdateStore } from "../../store";
import { AlertSubscriptionUpdate } from "../update/AlertSubscriptionUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type AlertSubscriptionModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const AlertSubscriptionModalContent: React.FC<AlertSubscriptionModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new AlertSubscriptionUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new AlertSubscriptionViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <AlertSubscriptionView
            alertSubscriptionViewStore={
              storeInfo.store as AlertSubscriptionViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <AlertSubscriptionUpdate
            alertSubscriptionUpdateStore={
              storeInfo.store as AlertSubscriptionUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
